import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { buildPraticaOggetto } from "../lib/oggetto";
import RichTextEditor from "./RichTextEditor";
import RubricaDialog from "./RubricaDialog";
import AllegaDaPraticaDialog from "./AllegaDaPraticaDialog";
import SuggeritiDestinatariDialog from "./SuggeritiDestinatariDialog";
import TestiRapidiPicker from "./TestiRapidiPicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { MailCheck, Loader2, Send, Paperclip, X, RefreshCw, AlertTriangle, BookUser, FolderInput } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  invio: { l: "In invio", c: "bg-slate-500/15 text-slate-300 border-slate-500/30" },
  inviata: { l: "Inviata", c: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  accettata: { l: "Accettata", c: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30" },
  consegnata: { l: "Consegnata", c: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  errore: { l: "Errore", c: "bg-red-500/15 text-red-400 border-red-500/30" },
};

const readFileB64 = (file) =>
  new Promise((resolve) => {
    const r = new FileReader();
    r.onload = () => resolve({ nome: file.name, mime: file.type || "application/octet-stream", content_b64: String(r.result).split(",")[1] });
    r.readAsDataURL(file);
  });

export default function PecDialog({ open, onOpenChange, pratica, initialAttachments, clienteId, soggetto, prefillTo, prefillSubject }) {
  const [tab, setTab] = useState("invia");
  const [status, setStatus] = useState(null); // {configured, enabled, mailbox}
  const [mode, setMode] = useState("libero"); // libero | template
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [rubricaTarget, setRubricaTarget] = useState(null); // "to" | "cc" | "bcc"
  const [suggOpen, setSuggOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [files, setFiles] = useState([]);
  const [praticaAtts, setPraticaAtts] = useState([]); // [{id, nome, content_type}] allegati esistenti
  const [allegaOpen, setAllegaOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const editorRef = useRef(null);
  const insertQuickText = (t) => {
    const html = String(t || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br>");
    if (editorRef.current) editorRef.current.chain().focus().insertContent(html).run();
    else setHtml((h) => (h || "") + html);
  };
  const [messages, setMessages] = useState([]);
  const [loadingMsg, setLoadingMsg] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const canSend = useMemo(() => status?.configured && status?.enabled, [status]);

  const loadStatus = async () => {
    try { const { data } = await api.get("/studio/pec/status"); setStatus(data); }
    catch { setStatus({ configured: false, enabled: false }); }
  };
  const loadTemplates = async () => {
    try { const { data } = await api.get("/studio/letter-templates"); setTemplates(data); } catch { /* noop */ }
  };
  const loadMessages = async () => {
    setLoadingMsg(true);
    try { const { data } = await api.get(`/studio/pec/messages`, { params: { pratica_id: pratica?.id } }); setMessages(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoadingMsg(false); }
  };

  useEffect(() => {
    if (open) {
      const defSubject = buildPraticaOggetto(pratica);
      setTab("invia"); setMode("libero"); setTemplateId(""); setTo(prefillTo || ""); setCc(""); setBcc(""); setSubject(prefillSubject || defSubject); setHtml(""); setFiles(initialAttachments && initialAttachments.length ? initialAttachments : []); setPraticaAtts([]);
      loadStatus(); loadTemplates(); loadMessages();
      if (pratica?.id && !prefillTo) setSuggOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const addFromSuggeriti = (chosen) => {
    const addrs = chosen.map((c) => c.address);
    const existing = to.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
    setTo(Array.from(new Set([...existing, ...addrs])).join(", "));
  };

  const chooseTemplate = async (id) => {
    setTemplateId(id);
    if (!id || !pratica?.id) return;
    try {
      const { data } = await api.post(`/studio/letter-templates/${id}/generate`, { pratica_id: pratica.id });
      setHtml(data.content || "");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    const encoded = await Promise.all(list.map(readFileB64));
    setFiles((f) => [...f, ...encoded]);
    e.target.value = "";
  };

  const send = async () => {
    const parse = (s) => s.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
    const recipients = parse(to);
    if (!recipients.length) { toast.error("Inserisci almeno un destinatario PEC"); return; }
    if (!subject.trim()) { toast.error("Inserisci l'oggetto"); return; }
    if (!html || html.replace(/<[^>]+>/g, "").trim() === "") { toast.error("Inserisci il testo del messaggio"); return; }
    setSending(true);
    try {
      await api.post("/studio/pec/send", {
        pratica_id: pratica?.id, to: recipients, cc: parse(cc), bcc: parse(bcc), subject: subject.trim(), html, attachments: files, attachment_ids: praticaAtts.map((a) => a.id),
        cliente_id: clienteId || null, soggetto: soggetto || "", nome: subject.trim(),
      });
      toast.success("PEC inviata correttamente");
      setTo(""); setCc(""); setBcc(""); setSubject(""); setHtml(""); setFiles([]); setPraticaAtts([]); setTemplateId(""); setMode("libero");
      setTab("storico");
      loadMessages();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const rubricaSetters = { to: [to, setTo], cc: [cc, setCc], bcc: [bcc, setBcc] };
  const addFromRubrica = (addresses) => {
    if (!rubricaTarget) return;
    const [cur, setter] = rubricaSetters[rubricaTarget];
    const existing = cur.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
    setter(Array.from(new Set([...existing, ...addresses])).join(", "));
  };
  const RUBRICA_LABELS = { to: "Destinatari", cc: "CC", bcc: "CCN" };

  const refreshReceipts = async () => {
    setRefreshing(true);
    try { await api.post("/studio/pec/poll"); await loadMessages(); toast.success("Stato ricevute aggiornato"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRefreshing(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <SuggeritiDestinatariDialog open={suggOpen} onClose={() => setSuggOpen(false)} praticaId={pratica?.id} mode="pec" onConfirm={addFromSuggeriti} />
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="pec-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2">
            <MailCheck className="h-5 w-5 text-amber-400" /> PEC{pratica?.numero_pratica ? ` · ${pratica.numero_pratica}` : ""}
          </DialogTitle>
        </DialogHeader>

        {!canSend && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-300 flex items-start gap-2" data-testid="pec-not-configured">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>PEC non configurata o disattivata. Il Titolare deve impostarla in <strong>Impostazioni Studio → PEC</strong> prima di inviare.</span>
          </div>
        )}

        <div className="flex gap-1 border-b border-slate-800">
          {[["invia", "Invia PEC"], ["storico", "Storico invii"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`pec-tab-${k}`}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? "border-amber-400 text-amber-400" : "border-transparent text-slate-400 hover:text-slate-200"}`}>
              {l}
            </button>
          ))}
        </div>

        {tab === "invia" ? (
          <div className="space-y-4 pt-2">
            <div className="flex gap-2">
              {[["libero", "Messaggio libero"], ["template", "Da template lettera"]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setMode(k)} data-testid={`pec-mode-${k}`}
                  className={`text-xs font-semibold rounded-md border px-3 py-1.5 transition-colors ${mode === k ? "border-amber-500 text-amber-400 bg-amber-500/10" : "border-slate-700 text-slate-300 hover:bg-slate-800/50"}`}>
                  {l}
                </button>
              ))}
            </div>

            {mode === "template" && (
              <div>
                <Label className="text-slate-300">Template</Label>
                <Select value={templateId} onValueChange={chooseTemplate}>
                  <SelectTrigger data-testid="pec-template-select" className="mt-1.5 bg-slate-900/60 border-slate-700"><SelectValue placeholder="Seleziona un template…" /></SelectTrigger>
                  <SelectContent className="bg-[#121824] border-slate-700 text-slate-100 max-h-72">
                    {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">Destinatari PEC <span className="text-slate-500 font-normal">(separa con virgola)</span></Label>
                <button type="button" onClick={() => setRubricaTarget("to")} data-testid="pec-rubrica-to" className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1"><BookUser className="h-3.5 w-3.5" /> Rubrica</button>
              </div>
              <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="controparte@pec.it, compagnia@pec.it" data-testid="pec-to" className="mt-1.5 bg-slate-900/60 border-slate-700" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-slate-300">CC</Label>
                  <button type="button" onClick={() => setRubricaTarget("cc")} data-testid="pec-rubrica-cc" className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1"><BookUser className="h-3.5 w-3.5" /> Rubrica</button>
                </div>
                <Input value={cc} onChange={(e) => setCc(e.target.value)} data-testid="pec-cc" className="mt-1.5 bg-slate-900/60 border-slate-700" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-slate-300">CCN</Label>
                  <button type="button" onClick={() => setRubricaTarget("bcc")} data-testid="pec-rubrica-bcc" className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1"><BookUser className="h-3.5 w-3.5" /> Rubrica</button>
                </div>
                <Input value={bcc} onChange={(e) => setBcc(e.target.value)} data-testid="pec-bcc" className="mt-1.5 bg-slate-900/60 border-slate-700" />
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Oggetto</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="pec-subject" className="mt-1.5 bg-slate-900/60 border-slate-700" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-slate-300">Testo del messaggio</Label>
                <TestiRapidiPicker onInsert={insertQuickText} testId="pec-testi-rapidi" />
              </div>
              <RichTextEditor value={html} onChange={setHtml} onEditorReady={(ed) => { editorRef.current = ed; }} />
            </div>

            <div>
              <Label className="text-slate-300 mb-1.5 block">Allegati</Label>
              <div className="flex flex-wrap items-center gap-2">
                <label className="inline-flex items-center gap-2 text-sm text-slate-300 cursor-pointer rounded-md border border-slate-700 px-3 py-2 hover:bg-slate-800/50 w-fit" data-testid="pec-attach-label">
                  <Paperclip className="h-4 w-4" /> Aggiungi allegati
                  <input type="file" multiple className="hidden" onChange={onFiles} data-testid="pec-attach-input" />
                </label>
                <button type="button" onClick={() => setAllegaOpen(true)} data-testid="pec-allega-da-pratica"
                  className="inline-flex items-center gap-2 text-sm text-amber-300 rounded-md border border-amber-500/40 px-3 py-2 hover:bg-amber-500/10 w-fit">
                  <FolderInput className="h-4 w-4" /> Allega dalla pratica
                </button>
              </div>
              {(files.length > 0 || praticaAtts.length > 0) && (
                <div className="mt-2 space-y-1">
                  {praticaAtts.map((f) => (
                    <div key={f.id} className="flex items-center justify-between gap-2 text-xs text-slate-300 bg-slate-900/50 rounded px-2 py-1" data-testid={`pec-pratica-att-${f.id}`}>
                      <span className="truncate flex items-center gap-1.5"><span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 shrink-0">pratica</span>{f.nome}</span>
                      <button type="button" onClick={() => setPraticaAtts((arr) => arr.filter((x) => x.id !== f.id))} className="text-slate-500 hover:text-red-400"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 text-xs text-slate-300 bg-slate-900/50 rounded px-2 py-1" data-testid={`pec-attach-${i}`}>
                      <span className="truncate">{f.nome}</span>
                      <button type="button" onClick={() => setFiles((arr) => arr.filter((_, idx) => idx !== i))} className="text-slate-500 hover:text-red-400"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <Button onClick={send} disabled={sending || !canSend} data-testid="pec-send-button" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Invia PEC
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 pt-2" data-testid="pec-storico">
            <div className="flex justify-end">
              <Button variant="outline" onClick={refreshReceipts} disabled={refreshing} data-testid="pec-refresh-button" className="h-9 border-slate-700 text-slate-200 hover:bg-slate-800">
                {refreshing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />} Aggiorna stato ricevute
              </Button>
            </div>
            {loadingMsg ? (
              <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
            ) : messages.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">Nessuna PEC inviata per questa pratica.</p>
            ) : (
              messages.map((m) => {
                const s = STATUS[m.status] || STATUS.inviata;
                return (
                  <div key={m.id} className="pgp-card p-3" data-testid={`pec-msg-${m.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm text-slate-100 font-medium truncate">{m.subject}</div>
                        <div className="text-xs text-slate-400 truncate">A: {(m.to || []).join(", ")}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{formatDateTime(m.created_at)}</div>
                        {m.last_error && <div className="text-[11px] text-red-400 mt-1">{m.last_error}</div>}
                      </div>
                      <Badge className={`${s.c} shrink-0`} data-testid={`pec-status-${m.id}`}>{s.l}</Badge>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </DialogContent>
      <RubricaDialog open={!!rubricaTarget} onOpenChange={(o) => !o && setRubricaTarget(null)} onConfirm={addFromRubrica} targetLabel={rubricaTarget ? RUBRICA_LABELS[rubricaTarget] : ""} />
      <AllegaDaPraticaDialog open={allegaOpen} onOpenChange={setAllegaOpen} praticaId={pratica?.id}
        principaleNome={pratica?.nome_assistito || pratica?.assistito_scheda?.ragione_sociale || ""}
        alreadySelected={praticaAtts.map((a) => a.id)}
        onConfirm={(chosen, letterAtts) => {
          setPraticaAtts(chosen.map((c) => ({ id: c.id, nome: c.nome, content_type: c.content_type })));
          if (letterAtts && letterAtts.length) setFiles((f) => { const names = new Set(f.map((x) => x.nome)); return [...f, ...letterAtts.filter((a) => !names.has(a.nome))]; });
        }} />
    </Dialog>
  );
}
